import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useTranslation } from "i18n";

import Button from "components/button";
import { getJobs } from "api/Jobs";
import useApi from "hooks/useApi";

import "./style.css";

const selectSx = {
  height: "48px",
  width: "100%",
  fontSize: "14px",
  borderRadius: "10px",
  background: "#fff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(37, 42, 49, 0.12)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(241, 94, 34, 0.45)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--primary-color)",
    borderWidth: "1px",
  },
  "& .MuiSelect-outlined": {
    padding: "12px 14px",
    fontSize: "14px",
  },
};

const Jobs = () => {
  const { t } = useTranslation();
  const getJobsList = useApi(getJobs);
  const navigate = useNavigate();

  const [locations, setLocations] = useState({
    selected: "",
    options: [],
  });

  const [jobsTitle, setJobsTitle] = useState({
    selected: "",
    options: [],
  });

  const [departments, setDepartments] = useState({
    selected: "",
    options: [],
  });

  const language = "en";

  useEffect(() => {
    window.scrollTo(0, 0);
    handleFetchJobs();
  }, []);

  const handleFetchJobs = async () => {
    const body = {
      title: jobsTitle.selected || null,
      department: departments.selected || null,
      location: locations.selected || null,
    };
    getJobsList.request({
      language,
      body,
    });
  };

  const handelNavigate = (item) => {
    navigate(`/job/${item?.careerId}`, {
      state: {
        careerDetails: item,
      },
    });
  };

  useEffect(() => {
    setLocations((prev) => ({
      ...prev,
      options: getJobsList?.data?.filterKeys?.location || [],
    }));
    setJobsTitle((prev) => ({
      ...prev,
      options: getJobsList?.data?.filterKeys?.title || [],
    }));
    setDepartments((prev) => ({
      ...prev,
      options: getJobsList?.data?.filterKeys?.department || [],
    }));
  }, [getJobsList?.data]);

  const handleReset = () => {
    setLocations((prev) => ({ ...prev, selected: "" }));
    setJobsTitle((prev) => ({ ...prev, selected: "" }));
    setDepartments((prev) => ({ ...prev, selected: "" }));
    getJobsList.request({
      language,
      body: {
        title: null,
        department: null,
        location: null,
      },
    });
  };

  const jobs = getJobsList?.data?.jobs || [];
  const hasFilters =
    Boolean(jobsTitle.selected) ||
    Boolean(departments.selected) ||
    Boolean(locations.selected);
  const isLoading = getJobsList?.loading;
  const openRolesLabel =
    jobs.length === 1
      ? t("jobs.openRole", { count: jobs.length })
      : t("jobs.openRoles", { count: jobs.length });

  return (
    <div className="careers-page">
      <section className="careers-hero">
        <div className="careers-hero__media" aria-hidden="true">
          <img
            src={require("assets/images/job-banner.png")}
            alt=""
          />
        </div>
        <div className="careers-hero__veil" aria-hidden="true" />
        <div className="container careers-hero__content">
          <p className="careers-hero__brand">Satyam</p>
          <h1 className="careers-hero__title">{t("jobs.heroTitle")}</h1>
          <p className="careers-hero__subtitle">{t("jobs.heroSubtitle")}</p>
        </div>
      </section>

      <div className="container careers-shell">
        <form
          className="careers-filters"
          onSubmit={(e) => {
            e.preventDefault();
            handleFetchJobs();
          }}
        >
          <div className="careers-filters__fields">
            <label className="careers-filters__field">
              <span>{t("jobs.filterJob")}</span>
              <Select
                displayEmpty
                value={jobsTitle.selected}
                onChange={(e) =>
                  setJobsTitle({
                    ...jobsTitle,
                    selected: e.target.value,
                  })
                }
                renderValue={(value) =>
                  value || t("jobs.selectJob")
                }
                sx={selectSx}
              >
                {jobsTitle?.options?.length === 0 ? (
                  <MenuItem disabled sx={{ fontStyle: "italic" }}>
                    {t("jobs.noResults")}
                  </MenuItem>
                ) : (
                  jobsTitle.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))
                )}
              </Select>
            </label>

            <label className="careers-filters__field">
              <span>{t("jobs.filterDepartment")}</span>
              <Select
                displayEmpty
                value={departments.selected}
                onChange={(e) =>
                  setDepartments({
                    ...departments,
                    selected: e.target.value,
                  })
                }
                renderValue={(value) =>
                  value || t("jobs.selectDepartment")
                }
                sx={selectSx}
              >
                {departments?.options?.length === 0 ? (
                  <MenuItem disabled sx={{ fontStyle: "italic" }}>
                    {t("jobs.noResults")}
                  </MenuItem>
                ) : (
                  departments.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))
                )}
              </Select>
            </label>

            <label className="careers-filters__field">
              <span>{t("jobs.filterLocation")}</span>
              <Select
                displayEmpty
                value={locations.selected}
                onChange={(e) =>
                  setLocations({
                    ...locations,
                    selected: e.target.value,
                  })
                }
                renderValue={(value) =>
                  value || t("jobs.selectLocation")
                }
                sx={selectSx}
              >
                {locations?.options?.length === 0 ? (
                  <MenuItem disabled sx={{ fontStyle: "italic" }}>
                    {t("jobs.noResults")}
                  </MenuItem>
                ) : (
                  locations.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))
                )}
              </Select>
            </label>
          </div>

          <div className="careers-filters__actions">
            <button
              type="submit"
              className="careers-filters__search"
              disabled={isLoading}
            >
              <SearchRoundedIcon fontSize="small" />
              {t("jobs.search")}
            </button>
            {hasFilters && (
              <Button
                type="button"
                className="careers-filters__reset"
                onClick={handleReset}
              >
                {t("jobs.reset")}
              </Button>
            )}
          </div>
        </form>

        <section className="careers-list">
          <div className="careers-list__header">
            <div>
              <h2>{t("jobs.openPositions")}</h2>
              <p>{t("jobs.openPositionsHint")}</p>
            </div>
            {!isLoading && jobs.length > 0 && (
              <span className="careers-list__count">{openRolesLabel}</span>
            )}
          </div>

          {isLoading ? (
            <div className="careers-skeleton-list">
              {[0, 1, 2].map((item) => (
                <div className="careers-skeleton" key={item}>
                  <Skeleton variant="rounded" height={22} width="42%" />
                  <Skeleton
                    variant="rounded"
                    height={16}
                    width="28%"
                    sx={{ mt: 1.5 }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={16}
                    width="22%"
                    sx={{ mt: 1 }}
                  />
                </div>
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="careers-empty">
              <WorkOutlineRoundedIcon className="careers-empty__icon" />
              <h3>{t("jobs.emptyTitle")}</h3>
              <p>{t("jobs.emptyMessage")}</p>
            </div>
          ) : (
            <ul className="careers-roles">
              {jobs.map((career, index) => (
                <li key={career?.careerId || index}>
                  <button
                    type="button"
                    className="careers-role"
                    style={{ "--role-delay": `${index * 45}ms` }}
                    onClick={() => handelNavigate(career)}
                  >
                    <div className="careers-role__main">
                      <h3>{career?.title}</h3>
                      <div className="careers-role__meta">
                        <span>
                          <ApartmentRoundedIcon fontSize="inherit" />
                          {career?.department}
                        </span>
                        <span>
                          <PlaceOutlinedIcon fontSize="inherit" />
                          {career?.location}
                        </span>
                      </div>
                    </div>
                    <span className="careers-role__cta">
                      {t("jobs.viewRole")}
                      <ArrowForwardRoundedIcon fontSize="small" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default Jobs;
